import type { ESPLoader } from "esptool-js";

import { md5 } from "./md5.ts";

/** The stub sends a read in packets of this size. */
const PACKET = 0x1000;
/** Packets the stub sends ahead of the acknowledgements, as esptool.py asks: more than a chunk holds. */
const IN_FLIGHT = 64;
/** One read command, checked by its MD5 and read again when bytes were lost. */
const CHUNK = 0x10000;
const TRIES = 4;
/** A pause in the board's data this long means the rest of a packet is not coming. */
const IDLE_MS = 2000;

/**
 * Read `length` bytes of flash at `offset` through the loader's stub, as
 * esptool.py's read_flash does, in chunks: the stub sends packets, each
 * acknowledged with the bytes received so far, then the MD5 of what it sent.
 * Web Serial loses a few bytes of a long read now and then, so a chunk whose
 * packets or MD5 do not match is read again.
 */
export async function readFlash(
    loader: ESPLoader,
    offset: number,
    length: number,
    progress: (read: number) => void,
): Promise<Uint8Array> {
    const data = new Uint8Array(length);
    for (let at = 0; at < length; at += CHUNK) {
        const size = Math.min(CHUNK, length - at);
        for (let attempt = 1; ; attempt++) {
            const part = await readChunk(loader, offset + at, size, (read) => progress(at + read)).catch(
                (e: unknown) => e,
            );
            if (part instanceof Uint8Array) {
                data.set(part, at);
                break;
            }
            if (attempt === TRIES) throw part;
            // Let whatever the stub still sends arrive, then drop it.
            await new Promise((resolve) => setTimeout(resolve, 100));
            loader.transport.flushInput();
        }
    }
    return data;
}

async function readChunk(
    loader: ESPLoader,
    offset: number,
    size: number,
    progress: (read: number) => void,
): Promise<Uint8Array> {
    const request = new Uint8Array(16);
    const view = new DataView(request.buffer);
    [offset, size, PACKET, IN_FLIGHT].forEach((value, i) => view.setUint32(i * 4, value, true));
    await loader.checkCommand("read flash", loader.ESP_READ_FLASH, request);
    const data = new Uint8Array(size);
    let received = 0;
    let damaged = false;
    for (let packets = 0; packets * PACKET < size; packets++) {
        const packet = await loader.transport.read(IDLE_MS).catch(() => undefined);
        if (packet === undefined) {
            damaged = true;
            break;
        }
        const expected = Math.min(PACKET, size - packets * PACKET);
        if (packet.length === expected) data.set(packet, packets * PACKET);
        else damaged = true;
        // Acknowledge what the stub sent rather than what arrived, so it goes on to its MD5 after a loss.
        received = Math.min(size, (packets + 1) * PACKET);
        await acknowledge(loader, received);
        progress(received);
    }
    if (received < size) await acknowledge(loader, size);
    const digest = await loader.transport.read(IDLE_MS);
    const sum = md5(data);
    if (damaged || digest.length !== sum.length || !digest.every((byte, i) => byte === sum[i])) {
        throw new Error(`Bytes of the flash read at 0x${offset.toString(16)} were lost on the way.`);
    }
    return data;
}

async function acknowledge(loader: ESPLoader, received: number): Promise<void> {
    const ack = new Uint8Array(4);
    new DataView(ack.buffer).setUint32(0, received, true);
    await loader.transport.write(ack);
}
